import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Loader2, RefreshCw, ChevronDown, ChevronUp, History, Crown } from 'lucide-react';

import { RootState, useAppDispatch } from '../store';
import {
  fetchFeatureStatus,
  fetchSettings,
  saveSettings,
  triggerRunNow,
  fetchRecentRuns,
  clearScheduleNotConfiguredFlag,
} from '../store/slices/autoScreenSlice';
import { showGlobalMessage } from '../store/slices/globalMessageSlice';
import { openUpgradePrompt } from '../store/slices/modalsSlice';
import { Button } from './ui/Button';
import { AutoScreenSettingsPatch } from '../services/api';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const POLL_INTERVAL_MS = 30_000;

const formatRelative = (iso: string | null, t: ReturnType<typeof useTranslation>['t']): string => {
  if (!iso) return t('autoScreen.status.never', { defaultValue: 'Never' });
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return t('autoScreen.status.unknown', { defaultValue: 'Unknown' });
  const diffMs = Date.now() - ts;
  if (diffMs < 0) {
    // Future timestamp (e.g. nextEligibleAt) - show "in N min".
    const future = Math.abs(diffMs);
    if (future < 60_000) return t('autoScreen.time.inSeconds', { defaultValue: 'in <1 min' });
    const mins = Math.round(future / 60_000);
    if (mins < 60) {
      return t('autoScreen.time.inMinutes', {
        defaultValue: `in ${mins} min`,
        count: mins,
      });
    }
    const hours = Math.round(mins / 60);
    return t('autoScreen.time.inHours', {
      defaultValue: `in ${hours} h`,
      count: hours,
    });
  }
  if (diffMs < 60_000) return t('autoScreen.time.justNow', { defaultValue: 'just now' });
  const mins = Math.round(diffMs / 60_000);
  if (mins < 60) {
    return t('autoScreen.time.minutesAgo', {
      defaultValue: `${mins} min ago`,
      count: mins,
    });
  }
  const hours = Math.round(mins / 60);
  if (hours < 24) {
    return t('autoScreen.time.hoursAgo', {
      defaultValue: `${hours} h ago`,
      count: hours,
    });
  }
  const days = Math.round(hours / 24);
  return t('autoScreen.time.daysAgo', { defaultValue: `${days} d ago`, count: days });
};

const skipReasonKey = (reason: string | null): string | null => {
  if (!reason) return null;
  // Map to a stable i18n key; unknown reasons fall through to a generic label.
  const map: Record<string, string> = {
    idle: 'autoScreen.skipReason.idle',
    off_hours: 'autoScreen.skipReason.offHours',
    tier: 'autoScreen.skipReason.tier',
    quota: 'autoScreen.skipReason.quota',
    no_filters: 'autoScreen.skipReason.noFilters',
    locked: 'autoScreen.skipReason.locked',
    cooldown: 'autoScreen.skipReason.cooldown',
    stale_data: 'autoScreen.skipReason.staleData',
  };
  return map[reason] ?? 'autoScreen.skipReason.generic';
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface AutoScreenSettingsCardProps {
  /** Optional override for the "View history" CTA. Defaults to no-op. */
  onViewHistory?: () => void;
}

const Toggle: React.FC<{
  id: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  label: string;
  description?: string;
}> = ({ id, checked, disabled, onChange, label, description }) => (
  <label
    htmlFor={id}
    className={
      'flex items-start gap-3 rounded-lg p-3 transition-colors ' +
      (disabled
        ? 'cursor-not-allowed opacity-50'
        : 'cursor-pointer hover:bg-surface-container-high')
    }
  >
    <input
      id={id}
      type="checkbox"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-0.5 h-5 w-5 rounded border-border-default bg-surface-container text-accent-primary focus:ring-2 focus:ring-accent-primary focus:ring-offset-0"
    />
    <span className="flex-1">
      <span className="block text-sm font-medium text-text-primary">{label}</span>
      {description && (
        <span className="mt-0.5 block text-xs text-text-secondary">{description}</span>
      )}
    </span>
  </label>
);

const AutoScreenSettingsCard: React.FC<AutoScreenSettingsCardProps> = ({ onViewHistory }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    featureEnabled,
    settings,
    recentRuns,
    status,
    errors,
    paidTierRequired,
    scheduleNotConfigured,
  } = useSelector((state: RootState) => state.autoScreen);

  // Collapsed by default in production (and Jest); expanded only in local dev (`npm start`).
  const [advancedOpen, setAdvancedOpen] = useState(process.env.NODE_ENV === 'development');

  // ---------------------------------------------------------------------------
  // Lifecycle: fetch feature flag on mount; if enabled, fetch settings + runs.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (featureEnabled === null) {
      dispatch(fetchFeatureStatus());
    }
  }, [dispatch, featureEnabled]);

  useEffect(() => {
    if (featureEnabled === true) {
      dispatch(fetchSettings());
      dispatch(fetchRecentRuns(20));
    }
  }, [dispatch, featureEnabled]);

  // ---------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------
  const latestRun = recentRuns[0] ?? null;
  const latestRunStatus = latestRun?.status ?? null;
  const runIsInFlight = useMemo(() => {
    if (settings?.runNowRequestedAt) return true;
    if (settings?.lastRunStatus === 'running' || settings?.lastRunStatus === 'queued') return true;
    if (latestRunStatus === 'running' || latestRunStatus === 'queued') return true;
    return false;
  }, [settings?.runNowRequestedAt, settings?.lastRunStatus, latestRunStatus]);

  // ---------------------------------------------------------------------------
  // Polling: while a run is in flight, refresh runs every 30s.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!runIsInFlight || featureEnabled !== true) return;
    const id = setInterval(() => {
      dispatch(fetchRecentRuns(5));
      dispatch(fetchSettings());
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [dispatch, runIsInFlight, featureEnabled]);

  // ---------------------------------------------------------------------------
  // 402 / 409 toast handling
  // ---------------------------------------------------------------------------
  // Dedup the modal open without clearing the slice flag - the footer
  // upgrade CTA needs `paidTierRequired` to remain truthy after the modal
  // open call, otherwise it renders for one frame and disappears. The flag
  // is reset only by `saveSettings.fulfilled` (a successful retry) or by
  // `resetAutoScreenState`.
  const upgradePromptFiredRef = useRef(false);
  useEffect(() => {
    if (paidTierRequired && !upgradePromptFiredRef.current) {
      upgradePromptFiredRef.current = true;
      dispatch(openUpgradePrompt('auto_screen'));
    }
    if (!paidTierRequired) {
      upgradePromptFiredRef.current = false;
    }
  }, [paidTierRequired, dispatch]);

  useEffect(() => {
    if (scheduleNotConfigured) {
      dispatch(
        showGlobalMessage({
          message: t('autoScreen.errors.scheduleNotConfigured', {
            defaultValue: 'Configure & enable auto-screen first.',
          }),
          type: 'warning',
        })
      );
      dispatch(clearScheduleNotConfiguredFlag());
    }
  }, [scheduleNotConfigured, dispatch, t]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handlePatch = useCallback(
    (patch: AutoScreenSettingsPatch) => {
      dispatch(saveSettings(patch));
    },
    [dispatch]
  );

  const handleRunNow = useCallback(() => {
    dispatch(triggerRunNow());
  }, [dispatch]);

  // ---------------------------------------------------------------------------
  // Kill switch: hide entire card when feature disabled or 503.
  // featureEnabled === null means we haven't fetched yet - render a tiny placeholder
  // so the layout doesn't jump and tests can still pivot on data-testid.
  // ---------------------------------------------------------------------------
  if (featureEnabled === false) {
    return null;
  }
  if (featureEnabled === null) {
    return (
      <div
        data-testid="auto-screen-card-loading"
        className="rounded-xl bg-surface-container-high p-4 text-text-secondary"
      >
        <Loader2 className="inline h-4 w-4 animate-spin" aria-hidden="true" />{' '}
        <span className="text-sm">
          {t('autoScreen.loading', { defaultValue: 'Loading auto-screen settings…' })}
        </span>
      </div>
    );
  }

  // featureEnabled === true but settings still loading - show skeleton card.
  if (!settings) {
    return (
      <div
        data-testid="auto-screen-card"
        className="rounded-xl bg-surface-container-high p-4 text-text-secondary"
      >
        <Loader2 className="inline h-4 w-4 animate-spin" aria-hidden="true" />{' '}
        <span className="text-sm">
          {t('autoScreen.loading', { defaultValue: 'Loading auto-screen settings…' })}
        </span>
        {errors.fetch && (
          <p className="mt-2 text-xs text-accent-bearish">
            {t('autoScreen.errors.fetchFailed', {
              defaultValue: 'Failed to load auto-screen settings.',
            })}
          </p>
        )}
      </div>
    );
  }

  const masterEnabled = settings.enabled;
  const channelsDisabled = !masterEnabled;
  const subTogglesDisabled = !masterEnabled;
  const saving = status.save === 'saving';

  // Status line text
  const statusLine = (() => {
    if (!settings.lastRunAt) {
      return t('autoScreen.status.noRunsYet', { defaultValue: 'No runs yet.' });
    }
    const when = formatRelative(settings.lastRunAt, t);
    const matched = latestRun?.matchedCount ?? 0;
    const newCount = latestRun?.newTickerCount ?? 0;
    if (settings.lastRunStatus === 'completed') {
      return t('autoScreen.status.lastCompleted', {
        defaultValue: `Last run ${when} - ${matched} matches (${newCount} new)`,
        when,
        matched,
        newCount,
      });
    }
    if (settings.lastRunStatus === 'failed') {
      return t('autoScreen.status.lastFailed', {
        defaultValue: `Last run ${when} - failed`,
        when,
      });
    }
    if (settings.lastRunStatus === 'skipped') {
      const reasonKey = skipReasonKey(latestRun?.skipReason ?? null);
      const reasonLabel = reasonKey
        ? t(reasonKey, { defaultValue: latestRun?.skipReason ?? 'skipped' })
        : t('autoScreen.skipReason.generic', { defaultValue: 'skipped' });
      return t('autoScreen.status.lastSkipped', {
        defaultValue: `Last run ${when} - skipped (${reasonLabel})`,
        when,
        reason: reasonLabel,
      });
    }
    if (settings.lastRunStatus === 'running' || settings.lastRunStatus === 'queued') {
      return t('autoScreen.status.running', {
        defaultValue: 'A run is in progress…',
      });
    }
    return t('autoScreen.status.lastGeneric', {
      defaultValue: `Last run ${when}`,
      when,
    });
  })();

  const nextLine = settings.nextEligibleAt
    ? t('autoScreen.status.nextEligible', {
        defaultValue: `Next: ${formatRelative(settings.nextEligibleAt, t)}`,
        when: formatRelative(settings.nextEligibleAt, t),
      })
    : null;

  return (
    <div
      data-testid="auto-screen-card"
      className="rounded-xl bg-surface-container-high p-4 md:p-5"
    >
      {/* Header row: title + master toggle */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-text-primary md:text-lg">
            {t('autoScreen.title', { defaultValue: 'Auto-screen every hour' })}
          </h3>
          <p className="mt-1 text-xs text-text-secondary md:text-sm">
            {t('autoScreen.subtitle', {
              defaultValue: 'Runs every hour in the background. Screening uses your saved filters.',
            })}
          </p>
        </div>
        <label
          htmlFor="auto-screen-master"
          className="inline-flex shrink-0 cursor-pointer items-center gap-2"
        >
          <span className="sr-only">
            {t('autoScreen.masterToggle.label', { defaultValue: 'Enable auto-screen' })}
          </span>
          <input
            id="auto-screen-master"
            type="checkbox"
            role="switch"
            aria-checked={masterEnabled}
            checked={masterEnabled}
            disabled={saving}
            onChange={(e) => handlePatch({ enabled: e.target.checked })}
            className="h-6 w-6 rounded-full border border-border-subtle bg-surface-container text-accent-primary focus:ring-2 focus:ring-accent-primary"
          />
        </label>
      </div>

      {/* Status block */}
      <div
        className="mt-3 rounded-lg bg-surface-container px-3 py-2 text-xs text-text-secondary md:text-sm"
        data-testid="auto-screen-status"
      >
        <div>{statusLine}</div>
        {nextLine && <div className="mt-0.5">{nextLine}</div>}
        {settings.consecutiveFailures > 0 && (
          <div className="mt-0.5 text-accent-warning">
            {t('autoScreen.status.consecutiveFailures', {
              defaultValue: `${settings.consecutiveFailures} consecutive failures - backoff active.`,
              count: settings.consecutiveFailures,
            })}
          </div>
        )}
      </div>

      {/* Sub-toggles - disabled when master OFF */}
      <div className="mt-3 space-y-1">
        <Toggle
          id="auto-screen-market-hours"
          checked={settings.marketHoursOnly}
          disabled={subTogglesDisabled || saving}
          onChange={(next) => handlePatch({ marketHoursOnly: next })}
          label={t('autoScreen.marketHoursOnly.label', {
            defaultValue: 'Only run during market hours',
          })}
        />
        <Toggle
          id="auto-screen-only-new"
          checked={settings.onlyNotifyOnNew}
          disabled={subTogglesDisabled || saving}
          onChange={(next) => handlePatch({ onlyNotifyOnNew: next })}
          label={t('autoScreen.onlyNotifyOnNew.label', {
            defaultValue: 'Only notify when there are new matches',
          })}
        />
      </div>

      {/* Notification channels - visually disabled until master ON */}
      <div className="mt-4">
        <h4 className="text-sm font-semibold text-text-primary">
          {t('autoScreen.notifications.title', {
            defaultValue: 'Notifications (all OFF by default)',
          })}
        </h4>
        <div className="mt-2 space-y-1">
          <Toggle
            id="auto-screen-notify-email"
            checked={settings.notifyEmail}
            disabled={channelsDisabled || saving}
            onChange={(next) => handlePatch({ notifyEmail: next })}
            label={t('autoScreen.channels.email', { defaultValue: 'Email' })}
          />
          <Toggle
            id="auto-screen-notify-telegram"
            checked={settings.notifyTelegram}
            disabled={channelsDisabled || saving}
            onChange={(next) => handlePatch({ notifyTelegram: next })}
            label={t('autoScreen.channels.telegram', { defaultValue: 'Telegram' })}
          />
          <Toggle
            id="auto-screen-notify-whatsapp"
            checked={settings.notifyWhatsapp}
            disabled={channelsDisabled || saving}
            onChange={(next) => handlePatch({ notifyWhatsapp: next })}
            label={t('autoScreen.channels.whatsapp', { defaultValue: 'WhatsApp' })}
          />
        </div>
      </div>

      {/* Advanced section */}
      <div className="mt-4">
        <button
          type="button"
          onClick={() => setAdvancedOpen((v) => !v)}
          className="flex items-center gap-1.5 text-sm font-medium text-text-secondary hover:text-text-primary"
          aria-expanded={advancedOpen}
          aria-controls="auto-screen-advanced"
        >
          {advancedOpen ? (
            <ChevronUp className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          )}
          {t('autoScreen.advanced.toggle', { defaultValue: 'Advanced' })}
        </button>
        {advancedOpen && (
          <div id="auto-screen-advanced" className="mt-2 space-y-1">
            <Toggle
              id="auto-screen-run-when-inactive"
              // UI maps "Run even if I'm not active" → !requireUserActive.
              checked={!settings.requireUserActive}
              disabled={subTogglesDisabled || saving}
              onChange={(next) => handlePatch({ requireUserActive: !next })}
              label={t('autoScreen.runWhenInactive.label', {
                defaultValue: "Run even if I'm not active",
              })}
              description={t('autoScreen.runWhenInactive.description', {
                defaultValue:
                  'By default, runs are skipped if you have not used the app in the last 24 hours.',
              })}
            />
          </div>
        )}
      </div>

      {/* Quota reset hint */}
      <p className="mt-3 text-xs text-text-muted">
        {t('autoScreen.quotaReset', {
          defaultValue: 'Daily scan quota resets at midnight UTC.',
        })}
      </p>

      {/* Footer CTAs */}
      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
        {paidTierRequired && (
          <Button
            type="button"
            variant="warning"
            size="sm"
            onClick={() => dispatch(openUpgradePrompt('auto_screen'))}
            leftIcon={<Crown className="h-4 w-4" aria-hidden="true" />}
            data-testid="auto-screen-upgrade-cta"
          >
            {t('autoScreen.upgradeCta', { defaultValue: 'Upgrade to enable' })}
          </Button>
        )}
        {onViewHistory && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onViewHistory}
            leftIcon={<History className="h-4 w-4" aria-hidden="true" />}
          >
            {t('autoScreen.viewHistory', { defaultValue: 'View history' })}
          </Button>
        )}
        <Button
          type="button"
          variant="primary"
          size="sm"
          disabled={runIsInFlight || saving || status.runNow === 'queueing' || !masterEnabled}
          onClick={handleRunNow}
          leftIcon={
            status.runNow === 'queueing' ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
            )
          }
          data-testid="auto-screen-run-now"
        >
          {runIsInFlight
            ? t('autoScreen.runNow.running', { defaultValue: 'Running…' })
            : t('autoScreen.runNow.label', { defaultValue: 'Run now' })}
        </Button>
      </div>

      {errors.save && !paidTierRequired && (
        <p className="mt-2 text-xs text-accent-bearish" role="alert">
          {t('autoScreen.errors.saveFailed', {
            defaultValue: 'Failed to save settings. Please try again.',
          })}
        </p>
      )}
      {errors.runNow && !paidTierRequired && !scheduleNotConfigured && (
        <p
          className="mt-2 text-xs text-accent-bearish"
          role="alert"
          data-testid="auto-screen-run-now-error"
        >
          {t('autoScreen.errors.runNowFailed', {
            defaultValue: 'Failed to queue run. Please try again.',
          })}
        </p>
      )}
    </div>
  );
};

export default AutoScreenSettingsCard;
